import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Modal, ModalTitle, ModalContent, Button } from 'src/components/core';
import { Cross, IDVerification } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { BASE_URL } from 'src/config';

interface CompleteKYCModalProps {
    open: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, colors, isMobile, breakpoints } }) => `
        .modal-paper {
            padding: ${isMobile ? '16px' : '24px'};
            @media (min-width: ${breakpoints.md}) {
                width: 397px;
            }
        }
        .modal-title {
            padding: 0px;
            > div {
                font-size: ${
                    isMobile
                        ? typography.size.mobile.heading.h5
                        : typography.size.desktop.heading.h5
                };
            }
        }
        .modal-content {
            padding: 32px 0 0 0;
            color: ${colors.text.primary};
        }
    `}
`;

const TitleText = styled.div`
    ${({ color }) => `
        color: ${color};
    `}
`;

const KYCContainer = styled(FlexContainer)`
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    border-radius: 10px;
    ${({ theme: { colors } }) => `
        border: 1px solid ${colors.border.light};
    `}
`;

const KYCContainerInternal = styled(FlexContainer)`
    flex-direction: column;
    width: 100%;
    text-align: center;
    ${({ theme: { typography, colors } }) => `
        & > div {
            font-size: ${typography.size.desktop.paragraph.p1};
            font-weight: ${typography.weight.medium};
        }
        & > span {
            padding-top: 8px;
            color: ${colors.text.tertiary};
            font-size: ${typography.size.desktop.paragraph.p2};
            line-height: ${typography.lineHeight[4]};
        }
    `}
`;

const CompleteKYCModal: FC<CompleteKYCModalProps> = ({ open, handleClose }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('futures_bonus_trade_kyc_modal_title')}</TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <KYCContainer>
                    <KYCContainerInternal>
                        <IDVerification />
                        <TitleText style={{ paddingTop: 20 }}>
                            {t('futures_bonus_trade_kyc_modal_heading')}
                        </TitleText>
                        <span>{t('futures_bonus_trade_kyc_modal_description')}</span>
                    </KYCContainerInternal>
                    <Button
                        fullWidth
                        onClick={() => (window.location.href = `${BASE_URL}/user/verification`)}
                    >
                        {t('verify_now')}
                    </Button>
                </KYCContainer>
            </ModalContent>
        </StyledModal>
    );
};

export { CompleteKYCModal };
